import _ from 'lodash';

import { Request } from 'express';

import { isLocalRequest } from 'utils/domainUtils';
import { logger } from '@tock/logging';

// AppSettings contains *per-environment* settings for consumer. These settings should be configured for different
// environments using environment variables (e.g. GA_KEY='UA-xxxxx-yy' should be set to override the GA_KEY value
// in this file).
//
// Per-business settings are not set here. Instead they are set in the redux store under
// state.app.config.

export const envType = {
  DEMO: 'demo',
  DEV: 'dev',
  PREPROD: 'preprod',
  PROD: 'prod',
  STAGING: 'staging',
};

const previewModes = {
  PREVIEW: 'preview',
  PUBLISH: 'publish',
};

interface ApiBaseUrls {
  INTERNAL_API_BASE: string;
  EXTERNAL_API_BASE: string;
  WEB_SOCKET_BASE: string;
}

/**
 * We need to be able to specify a specific API_BASE override that only applies
 * to server-side rendering (so it can point at a cluster-local host). By default
 * this method uses the existing calcluation.
 *
 * More specifically, the API_BASE value of the AppConfig has to change during the
 * server-side rendering process. Because the code that needs to do the override
 * doesn't have access to the saved environment saved in prod.js / dev.js, we
 * calculate both here and apply the correct override as-needed. See renderPage.ts.
 *
 * TODO(bmt): Simplify this code. Now that we're not clustering, we should be able
 * to use process.env directly or create our own abstraction for it.
 */
function getApiBaseUrls(env: Dictionary<string>, req: Request | undefined): ApiBaseUrls {
  const defaultApiBase = getDefaultApiBase('https', env, req);
  const defaultWebSocketBase = getDefaultApiBase('wss', env, req);
  return {
    INTERNAL_API_BASE: env.INTERNAL_API_BASE || defaultApiBase,
    EXTERNAL_API_BASE: env.EXTERNAL_API_BASE || defaultApiBase,
    WEB_SOCKET_BASE: env.WEB_SOCKET_BASE || defaultWebSocketBase,
  };
}

/**
 * Calculates the default api base.
 *
 * For local server render.
 *  - Use localhost if using a local api server.
 *  - Use staging otherwise.
 *
 * For other server renders:
 *  - Use a host-relative path.
 *
 * For client rendering:
 *  - Use the API_BASE that was provided by the environment (usually a serialization of AppSettings).
 *
 */
function getDefaultApiBase(
  scheme: 'https' | 'wss',
  env: Dictionary<string>,
  req: Request | undefined
): string {
  const path = scheme === 'https' ? '/api' : '/ws';
  const fallbackEnvKey = scheme === 'https' ? 'API_BASE' : 'WSS_BASE';
  const stagingApiBase = `${scheme}://staging.exploretock.com${path}`;

  if (process.env.NODE_ENV === 'test') {
    return 'http://example.com';
  }

  if (process.env.TARGET_ENV === 'PR') {
    // for the PR env, there's no real ambiguity:
    return stagingApiBase;
  } else if (process.env.SERVER && req) {
    if (!isLocalRequest(req)) {
      return `${scheme}://${req.headers.host}${path}`;
    } else if (env.LOCAL_SERVER === 'true') {
      const insecureScheme = scheme === 'https' ? 'http' : 'ws';
      const localPort = scheme === 'https' ? 6060 : 6061;
      return `${insecureScheme}://localhost:${localPort}${path}`;
    } else {
      return stagingApiBase;
    }
  } else if (env[fallbackEnvKey] || !req) {
    return env[fallbackEnvKey];
  } else {
    throw new Error('Environment not configured properly, this should not happen');
  }
}

function computeLogLevel(env: Dictionary<string>, req?: Request): LogLevel {
  if (env.LOG_LEVEL || !req) {
    return env.LOG_LEVEL as LogLevel;
  }

  if (process.env.SERVER) {
    const isLocal = isLocalRequest(req);
    const isLocalOrStaging = isLocal || _.includes(req.headers.host, '-staging.');

    if (isLocalOrStaging) {
      return 'NETWORKING';
    }
    return 'INFO';
  }

  throw new Error('Log level not configured properly, this should not happen');
}

function computeExplorePath(env: Dictionary<string>, req?: Request) {
  if (env.EXPLORE_PATH || !req) {
    return env.EXPLORE_PATH;
  }

  const envPath = computeEnv(env, req);

  if (envPath === envType.PROD) {
    return 'www.exploretock.com';
  } else if (envPath === envType.DEV) {
    return `staging.exploretock.com`;
  } else {
    return `${envPath}.exploretock.com`;
  }
}

function computeConsumerUrlBase(env: Dictionary<string>, req?: Request) {
  const envPath = computeEnv(env, req);

  if (envPath === envType.PROD) {
    return 'https://www.exploretock.com';
  } else if (envPath === envType.DEV) {
    return `http://localhost:7001`;
  } else {
    return `https://${envPath}.exploretock.com`;
  }
}

function computeJceWebBaseUrl(env: Dictionary<string>, req?: Request) {
  const envPath = computeEnv(env, req);

  if (envPath === envType.DEV) {
    return 'http://localhost:5173';
  }

  return 'https://jce-web.e1.resy.com';
}

function computeDatadogEnv(env: Dictionary<string>, req?: Request) {
  if (env.DATADOG_ENV || !req) {
    return env.DATADOG_ENV;
  }
  return computeEnv(env, req);
}

// TODO - compute only based on url and not based on req
export function computeEnv(env: Dictionary<string>, req?: Request) {
  if (env.ENV || !req) {
    return env.ENV;
  }

  if (process.env.SERVER) {
    const isLocal = isLocalRequest(req);
    const isStaging = _.includes(req.headers.host, 'staging.');
    const isDemo = _.includes(req.headers.host, 'demo.');
    const isPreProd = _.includes(req.headers.host, 'preprod.');

    if (isLocal) {
      return envType.DEV;
    } else if (isStaging) {
      return envType.STAGING;
    } else if (isDemo) {
      return envType.DEMO;
    } else if (isPreProd) {
      return envType.PREPROD;
    } else {
      return envType.PROD;
    }
  }

  throw new Error('Env not configured properly, this should not happen');
}

function getPreviewMode(env: Dictionary<string>, req?: Request) {
  if (!req) {
    return env['PREVIEW_MODE'];
  }
  const isPreview = _.includes(req.headers.host, 'preview');
  return isPreview ? previewModes.PREVIEW : previewModes.PUBLISH;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function get(env: Dictionary<string>, value: string, defaultValue?: any) {
  if (value in env) {
    return env[value];
  }
  logger.debug(`${value} not set, using default: ${defaultValue}`);
  return defaultValue;
}

/**
 * env is a combination of process.env and argv(command line arguments, which override process.env)
 */
export function getSettings(env: Dictionary<string>, req?: Request): AppSettings {
  const apiUrls = getApiBaseUrls(env, req);
  return {
    // To use your local server, set the system environment variable LOCAL_SERVER=true
    AMPLITUDE_KEY: get(env, 'AMPLITUDE_KEY', 'fd5a18113ea9e58a4141e8cbc9fcd59f'),
    API_BASE: process.env.SERVER ? apiUrls.INTERNAL_API_BASE : apiUrls.EXTERNAL_API_BASE,
    EXTERNAL_API_BASE: apiUrls.EXTERNAL_API_BASE,
    INTERNAL_API_BASE: apiUrls.INTERNAL_API_BASE,
    CONSUMER_URL_BASE: computeConsumerUrlBase(env, req),
    DEFAULT_FETCH_TIMEOUT: get(env, 'DEFAULT_FETCH_TIMEOUT', 30000),
    EXPERIMENTS: get(env, 'EXPERIMENTS', ''),
    EXPLORE_PATH: computeExplorePath(env, req),
    GA_KEY: get(env, 'GA_KEY', 'UA-57200812-19'),
    GA4_KEY: get(env, 'GA4_KEY', 'G-LQ9PDWS8NJ'),
    GLEAP_KEY: get(env, 'GLEAP_KEY', 'ehLnS25MTrVgJOch9TKx00ACVljfVwXD'),
    JCE_WEB_BASE_URL: computeJceWebBaseUrl(env, req),
    GOOGLE_API_KEY: get(env, 'GOOGLE_API_KEY', 'AIzaSyAtcsSG3i0fmv5g5HVj6Kq7dlWNL7u7EK0'),
    GOOGLE_MAPS_KEY: get(env, 'GOOGLE_MAPS_KEY', 'AIzaSyDrvg7Eh2vWBDr_gnSZz7PqQF5z27uDSH0'),
    GOOGLE_OPTIMIZE_CONTAINER_ID: get(env, 'GOOGLE_OPTIMIZE_CONTAINER_ID', 'GTM-KFX6RWW'),
    // This is the "demo" configuration; it gets overridden via production config
    // for staging and for production:
    GOOGLE_TAG_MANAGER_ID: get(env, 'GOOGLE_TAG_MANAGER_ID', 'GTM-TC5QKVT'),
    FACEBOOK_PIXEL_ID: get(env, 'FACEBOOK_PIXEL_ID', '1258095530951991'),
    LOG_LEVEL: computeLogLevel(env, req),
    REVALIDATE_THRESHOLD_CENTS: get(env, 'REVALIDATE_THRESHOLD_CENTS', 0),
    ROLLBAR_ACCESS_TOKEN: get(env, 'ROLLBAR_ACCESS_TOKEN', '6e85c3d9390e4ddb8fd7b71153b2b06f'),
    ROLLBAR_ENV: computeDatadogEnv(env, req),
    STRIPE_PUBLIC_KEY: get(
      env,
      'STRIPE_PUBLIC_KEY',
      'pk_test_51Tg5QQK0bopwblSlqf6KOFrjVJNvKDaEYOlDJcHurinYxJEUFUHogEH812ERz6QBXrZNvFQpJk4aQ0dtyAIox5Yq00RhYnuRLx'
    ),
    TURNSTILE_SITE_KEY: get(env, 'TURNSTILE_SITE_KEY', '0x4AAAAAAAJHWaWliqwIW5o3'),
    WSS_BASE: apiUrls.WEB_SOCKET_BASE,
    USER_LOCALE: get(env, 'USER_LOCALE', 'en-us'),
    ZONE: get(env, 'ZONE', 'us-central1'),
    ENV: computeEnv(env, req),
    PREVIEW_MODE: getPreviewMode(env, req),
    DATADOG_ENV: computeDatadogEnv(env, req),
    DATADOG_CLIENT_TOKEN: get(env, 'DATADOG_CLIENT_TOKEN', 'pubb55f31573b5e4b7fad5f05527067cc6c'),
    DATADOG_CONSUMER_APP_ID: get(
      env,
      'DATADOG_CONSUMER_APP_ID',
      'c1880883-e79c-4ca1-b43f-34a8e5c13452'
    ),
  };
}

export interface AppSettings {
  AMPLITUDE_KEY?: string;
  API_BASE: string;
  // We need a different API_BASE during server-side rendering (where we go direct to an internal host)
  // then on the client where we have to hit a relative path. Unfortunately, that means API_BASE needs
  // change during the rendering process because we have to use the client base when rendering the chrome.
  EXTERNAL_API_BASE: string;
  INTERNAL_API_BASE: string;
  CONSUMER_URL_BASE: string;
  DEFAULT_FETCH_TIMEOUT: number;
  EXPERIMENTS: string;
  EXPLORE_PATH: string;
  FACEBOOK_PIXEL_ID: string;
  GA_KEY: string;
  GA4_KEY: string;
  GLEAP_KEY: string;
  JCE_WEB_BASE_URL: string;
  GOOGLE_API_KEY: string;
  GOOGLE_MAPS_KEY: string;
  GOOGLE_OPTIMIZE_CONTAINER_ID: string;
  GOOGLE_TAG_MANAGER_ID: string;
  LOG_LEVEL: LogLevel;
  REVALIDATE_THRESHOLD_CENTS: number;
  ROLLBAR_ACCESS_TOKEN: string;
  ROLLBAR_ENV: string;
  STRIPE_PUBLIC_KEY: string;
  TURNSTILE_SITE_KEY: string;
  USER_LOCALE: string;
  WSS_BASE: string;
  ZONE: string;
  ENV: string;
  PREVIEW_MODE: string;
  DATADOG_CLIENT_TOKEN: string;
  DATADOG_CONSUMER_APP_ID: string;
  DATADOG_ENV: string;
}
